/**
 * Ressources de l'API SwissTech, regroupées par domaine métier.
 *
 * Chaque fonction correspond à un point d'entrée du backend et retourne les
 * données déjà désérialisées.
 */
import { adresseApi, api, listeComplete } from './client';
import { repereVisiteur } from '../lib/visiteur';
import { dateVersIso } from './mappers';

/** Clients et remises négociées. */
export const clientsApi = {
  /**
   * @param {{recherche?: string, page?: number, size?: number}} [params] filtres et pagination
   * @returns {Promise<object>} page de clients
   */
  liste: (params) => api.get('/clients', params),
  /**
   * @returns {Promise<object[]>} tous les clients, page après page
   */
  toutes: () => listeComplete('/clients'),
  /**
   * @param {number} id identifiant du client
   * @returns {Promise<object>} le client
   */
  parId: (id) => api.get(`/clients/${id}`),
  /**
   * @param {object} client données du client
   * @returns {Promise<object>} le client créé
   */
  cree: (client) => api.post('/clients', client),
  /**
   * Depose un extrait au dossier d'un client.
   *
   * <p>Le fichier part tel quel, en piece jointe : il vit hors de la base, comme
   * les photos de chantier, et la fiche n'en garde que la cle.
   *
   * @param {number} id identifiant du client
   * @param {'REGISTRE_COMMERCE'|'POURSUITES'} piece piece visee
   * @param {Blob} fichier extrait a deposer
   * @param {string} [nomFichier] nom du fichier tel que remis
   * @returns {Promise<object>} le client mis a jour
   */
  deposePiece: (id, piece, fichier, nomFichier) => {
    const corps = new FormData();
    corps.append('fichier', fichier, nomFichier || 'extrait.pdf');
    return api.post(`/clients/${id}/pieces/${piece}`, corps);
  },
  /**
   * @param {number} id identifiant du client
   * @param {'REGISTRE_COMMERCE'|'POURSUITES'} piece piece a retirer
   * @returns {Promise<null>} rien
   */
  retirePiece: (id, piece) => api.delete(`/clients/${id}/pieces/${piece}`),
  /**
   * @param {number} id identifiant du client
   * @param {object} client nouvelles données
   * @returns {Promise<object>} le client mis à jour
   */
  metAJour: (id, client) => api.put(`/clients/${id}`, client),
  /**
   * @param {number} id identifiant du client
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/clients/${id}`),
  /**
   * @param {number} id identifiant du client
   * @param {string} reference référence de l'article
   * @returns {Promise<number>} la remise applicable, en pourcentage
   */
  remise: (id, reference) => api.get(`/clients/${id}/remise`, { reference }),
  /**
   * Envoie le catalogue du magasin à plusieurs personnes de la société, un
   * e-mail chacune, avec si demandé son lien d'ouverture de compte.
   *
   * @param {number} id identifiant du client
   * @param {{avecRemises: boolean, ttc: boolean, joindrePdf: boolean, inviter: boolean,
   *   destinataires: {nom: string, email: string, fonction?: string}[]}} demande ce qui part et à qui
   * @returns {Promise<object[]>} pour chaque personne, si le message est parti
   */
  envoieCatalogue: (id, demande) => api.post(`/clients/${id}/catalogue/envoi`, demande),
  /** Le catalogue neutre, au prix public, à n'importe quelles adresses. */
  envoieCatalogueNeutre: (demande) => api.post('/catalogue/envoi', demande),
};

/** Devis. */
export const devisApi = {
  /**
   * @param {{statut?: string, page?: number, size?: number}} [params] filtres et pagination
   * @returns {Promise<object>} page de devis
   */
  liste: (params) => api.get('/devis', params),
  /**
   * @returns {Promise<object[]>} tous les devis, page après page
   */
  toutes: () => listeComplete('/devis'),
  /**
   * @param {number} id identifiant du devis
   * @returns {Promise<object>} le devis, lignes et totaux compris
   */
  parId: (id) => api.get(`/devis/${id}`),
  /**
   * @param {number} clientId identifiant du client
   * @returns {Promise<object[]>} les devis du client
   */
  parClient: (clientId) => api.get(`/devis/client/${clientId}`),
  /**
   * @param {object} devis données du devis
   * @returns {Promise<object>} le devis créé
   */
  cree: (devis) => api.post('/devis', devis),
  /**
   * @param {number} id identifiant du devis
   * @param {object} devis nouvelles données
   * @returns {Promise<object>} le devis mis à jour
   */
  metAJour: (id, devis) => api.put(`/devis/${id}`, devis),
  /**
   * @param {number} id identifiant du devis
   * @param {{signataire?: string, signature?: string}} [signature] données de signature
   * @returns {Promise<object>} le devis accepté
   */
  accepte: (id, signature) => api.post(`/devis/${id}/acceptation`, signature ?? {}),
  /**
   * Ouvre le chantier d'une offre acceptée restée sans chantier, ou rend celui
   * qui existe : jamais deux.
   *
   * @param {number} id identifiant de l'offre
   * @returns {Promise<{chantierId: number}>} le chantier
   */
  ouvreChantier: (id) => api.post(`/devis/${id}/chantier`),
  /**
   * Établit la facture d'acompte d'une offre acceptée, ou rend celle qui existe.
   *
   * @param {number} id identifiant de l'offre
   * @returns {Promise<object>} la facture d'acompte
   */
  factureAcompte: (id) => api.post(`/devis/${id}/facture-acompte`),
  /**
   * Prépare une facture d'avancement (situation des travaux) : le pourcentage
   * de l'offre et des avenants, moins ce qui est déjà facturé.
   *
   * @param {number} id offre acceptée
   * @param {number} pourcent avancement des travaux, de 1 à 100
   * @returns {Promise<object>} la facture, en brouillon
   */
  factureAvancement: (id, pourcent) => api.post(`/devis/${id}/facture-avancement`, { pourcent }),
  /**
   * @param {number} id identifiant du devis
   * @returns {Promise<object>} le devis refusé
   */
  refuse: (id) => api.post(`/devis/${id}/refus`),
  /** Le client a accepté par erreur : l'offre redevient refusée, motif à l'appui. */
  annuleAcceptation: (id, motif) => api.post(`/devis/${id}/annule-acceptation`, { motif }),
  /**
   * Le client a refusé par erreur : l'offre redevient en attente.
   *
   * @param {number} id identifiant de l'offre
   * @returns {Promise<object>} l'offre, de nouveau en attente
   */
  annuleRefus: (id) => api.post(`/devis/${id}/annule-refus`),
  /**
   * @param {number} id identifiant du devis
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/devis/${id}`),
};

/** Factures et QR-facture. */
export const facturesApi = {
  /**
   * @param {{statut?: string, page?: number, size?: number}} [params] filtres et pagination
   * @returns {Promise<object>} page de factures
   */
  liste: (params) => api.get('/factures', params),
  /**
   * @returns {Promise<object[]>} tout, page après page
   */
  toutes: () => listeComplete('/factures'),
  /**
   * @param {number} id identifiant de la facture
   * @returns {Promise<object>} la facture, lignes et totaux compris
   */
  parId: (id) => api.get(`/factures/${id}`),
  /**
   * @returns {Promise<object[]>} les factures en attente de paiement
   */
  ouvertes: () => api.get('/factures/ouvertes'),
  /**
   * @param {number} clientId identifiant du client
   * @returns {Promise<object[]>} les factures du client
   */
  parClient: (clientId) => api.get(`/factures/client/${clientId}`),
  /**
   * @param {number} id identifiant de la facture
   * @returns {Promise<object>} les données de la QR-facture
   */
  qrFacture: (id) => api.get(`/factures/${id}/qr-facture`),
  /**
   * @param {object} facture données de la facture
   * @returns {Promise<object>} la facture créée
   */
  cree: (facture) => api.post('/factures', facture),
  /**
   * @param {number} devisId identifiant du devis accepté
   * @returns {Promise<object>} la facture créée
   */
  depuisDevis: (devisId) => api.post(`/factures/depuis-devis/${devisId}`),
  /**
   * @param {number} id identifiant de la facture
   * @param {object} facture nouvelles données
   * @returns {Promise<object>} la facture mise à jour
   */
  metAJour: (id, facture) => api.put(`/factures/${id}`, facture),
  /**
   * @param {number} id identifiant de la facture
   * @param {string} texte message imprimé tout en bas, vide pour l'enlever
   * @returns {Promise<object>} la facture à jour, même payée
   */
  messagePied: (id, texte) => api.put(`/factures/${id}/message-pied`, { texte }),
  /**
   * @param {number} id identifiant de la facture
   * @param {string} [datePaiement] date d'encaissement au format ISO
   * @returns {Promise<object>} la facture soldée
   */
  marquePayee: (id, datePaiement) =>
    api.post(`/factures/${id}/paiement`, undefined, { params: { datePaiement } }),
  /**
   * Les paiements reçus sur les factures, chacun à sa date.
   *
   * @returns {Promise<{factureId: number, date: string, montant: number}[]>} les paiements
   */
  encaissements: () => api.get('/factures/encaissements'),
  /**
   * Réécrit les paiements reçus d'une facture : l'acompte de décembre, le
   * solde de février…
   *
   * @param {number} id identifiant de la facture
   * @param {{date: string, montant: number}[]} paiements paiements, date ISO
   * @returns {Promise<object>} la facture à jour
   */
  remplaceEncaissements: (id, paiements) => api.put(`/factures/${id}/encaissements`, paiements),
  /**
   * @param {number} id identifiant de la facture
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/factures/${id}`),
};

/** Chantiers et coûts de revient. */
export const chantiersApi = {
  /**
   * @param {{statut?: string, page?: number, size?: number}} [params] filtres et pagination
   * @returns {Promise<object>} page de chantiers
   */
  liste: (params) => api.get('/chantiers', params),
  /**
   * @param {number} id identifiant du chantier
   * @returns {Promise<object>} le chantier
   */
  parId: (id) => api.get(`/chantiers/${id}`),
  /**
   * @param {number} id identifiant du chantier
   * @returns {Promise<object>} le détail des coûts et de la marge
   */
  couts: (id) => api.get(`/chantiers/${id}/couts`),
  /**
   * Prend du matériel au dépôt pour le chantier, ou l'y rapporte : le stock et
   * le coût du chantier bougent ensemble, au prix d'achat lu par le serveur.
   *
   * @param {number} id identifiant du chantier
   * @param {{lignes: {articleId: number, quantite: number}[], retour?: boolean}} demande
   *   articles et quantités
   * @returns {Promise<object>} le chantier, avec son matériel à jour
   */
  depot: (id, demande) => api.post(`/chantiers/${id}/depot`, demande),
  /**
   * Rapporte au dépôt le reste d'un achat fait chez un fournisseur pour le
   * chantier : le stock monte, le coût du chantier baisse au prix payé.
   *
   * @param {number} id identifiant du chantier
   * @param {number} achatId ligne de matériel acheté
   * @param {{quantite: number, employeId?: number}} demande quantité rapportée
   * @returns {Promise<object>} le chantier, avec son matériel à jour
   */
  rapporteAchat: (id, achatId, demande) =>
    api.post(`/chantiers/${id}/materiel/${achatId}/retour-depot`, demande),
  /**
   * @param {object} chantier données du chantier
   * @returns {Promise<object>} le chantier créé
   */
  cree: (chantier) => api.post('/chantiers', chantier),
  /**
   * @param {number} id identifiant du chantier
   * @param {object} chantier nouvelles données
   * @returns {Promise<object>} le chantier mis à jour
   */
  metAJour: (id, chantier) => api.put(`/chantiers/${id}`, chantier),
  /**
   * @param {number} id identifiant du chantier
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/chantiers/${id}`),
};

/** Équipe et paie. */
/**
 * Espace personnel de l'employe connecte.
 *
 * <p>La fiche complete des employes porte le numero AVS, l'IBAN et le salaire
 * de chacun : elle est fermee aux ouvriers. Leur espace lit donc la sienne ici,
 * ou la session designe la fiche, non la requete.
 */
export const monEspaceApi = {
  /** @returns {Promise<object>} la fiche de l'employe connecte */
  fiche: () => api.get('/mon-espace'),
  /** @returns {Promise<object[]>} les bons d'achat envoyés à l'employé */
  bonsAchat: () => api.get('/mon-espace/bons-achat'),
  /** @param {number} id bon ouvert @returns {Promise<object>} le bon, vu */
  bonAchatVu: (id) => api.post(`/mon-espace/bons-achat/${id}/vu`),
  /**
   * @param {number} id bon acheté
   * @param {object} achat rangs des matériaux manquants, montant et photo du ticket
   * @returns {Promise<object>} le bon, acheté
   */
  bonAchatAchete: (id, achat) => api.post(`/mon-espace/bons-achat/${id}/achat`, achat),
  /**
   * @param {object} fiche pointage, position, signature et messages
   * @returns {Promise<object>} la fiche mise a jour
   */
  enregistre: (fiche) => api.put('/mon-espace', fiche),
  /**
   * Ajoute un message au fil, sans le réécrire : la réponse de la direction,
   * arrivée entre-temps, n'est plus effacée.
   *
   * @param {string} texte contenu du message
   * @returns {Promise<object>} le message enregistré
   */
  ecrit: (texte) => api.post('/mon-espace/messages', { texte }),
  /**
   * L'employé a ouvert sa messagerie : les réponses de la direction sont lues,
   * pour son compte seulement.
   *
   * @returns {Promise<null>} rien
   */
  messagesLus: () => api.post('/mon-espace/messages/lus'),
  /**
   * Les collègues en activité, nommés seulement : le magasinier choisit à qui
   * il remplit la feuille du dépôt.
   *
   * @returns {Promise<Array<{id: number, nom: string}>>} les collègues
   */
  collegues: () => api.get('/mon-espace/collegues'),
  /**
   * La liste des rendez-vous est fermee a l'ouvrier, parce qu'elle nomme les
   * clients : il lit ici les siens, sans le client ni les notes du bureau.
   *
   * @returns {Promise<object[]>} les rendez-vous auxquels il est convoque
   */
  convocations: () => api.get('/mon-espace/rendez-vous'),
  /**
   * La paie est fermee a l'ouvrier, parce qu'elle porte les salaires de tous :
   * il lit ici ses seules fiches, telles que la direction les a emises. Le
   * PDF de chacune se telecharge sous `/mon-espace/fiches-paie/{id}/document`.
   *
   * @returns {Promise<object[]>} ses fiches emises ({ id, mois, emisLe }), la plus recente d'abord
   */
  fichesPaie: () => api.get('/mon-espace/fiches-paie'),
  /**
   * Ses lettres et certificats : lettre de resiliation, demission remise,
   * certificats. Le PDF de chacun s'ouvre sous `/mon-espace/documents/{id}`.
   *
   * @returns {Promise<{id: number, type: string, titre: string, emisLe: string}[]>} ses documents
   */
  documents: () => api.get('/mon-espace/documents'),
  /**
   * Les bons de livraison sont fermes a l'ouvrier, parce qu'ils portent les
   * prix : il lit ici sa tournee, sans prix.
   *
   * @returns {Promise<object[]>} les bons a livrer
   */
  livraisons: () => api.get('/mon-espace/livraisons'),
  /**
   * Etat de la signature de son contrat : signe ou non, et si le contrat a
   * change depuis.
   *
   * @returns {Promise<object>} l'etat ({ signe, signeLe, nouvelleSignatureDemandee, aSigner })
   */
  contrat: () => api.get('/mon-espace/contrat'),
  /**
   * Signe son contrat : le serveur fige le PDF signe au dossier.
   *
   * @param {string} signature trace de la signature, en commandes de chemin SVG
   * @returns {Promise<object>} l'etat, desormais signe
   */
  signeContrat: (signature) =>
    api.post('/mon-espace/contrat/signature', { signature, accepte: true }),
  /**
   * @param {number} id bon livre
   * @param {number[]} manquantes lignes decochees, qui restent a livrer
   * @returns {Promise<object>} le bon, Livree ou Partielle
   */
  valideLivraison: (id, manquantes) =>
    api.post(`/mon-espace/livraisons/${id}/validation`, { manquantes }),
  /**
   * Les depenses de la societe sont fermees a l'ouvrier : il declare ici son
   * seul achat fournisseur, dont la depense se range en comptabilite sur le
   * chantier, avec la photo du bon pour justificatif.
   *
   * @param {{chantierId: number, fournisseur?: string, montant: number, libelle?: string,
   *          justificatif?: string, justificatifNom?: string}} achat ce qu'il declare
   * @returns {Promise<object>} la depense creee
   */
  achatFournisseur: (achat) => api.post('/mon-espace/achats-fournisseur', achat),
  /**
   * La cloche de l'employé : messages, rendez-vous, plans, documents…
   *
   * @returns {Promise<object[]>} ses signaux des trois derniers mois
   */
  signaux: () => api.get('/mon-espace/signaux'),
  /** @param {number} id signal ouvert @returns {Promise<object>} le signal, vu */
  signalVu: (id) => api.post(`/mon-espace/signaux/${id}/vu`),
  /**
   * @param {string[]} [types] sortes à éteindre ; toutes si absent
   * @returns {Promise<{marques: number}>} combien ont été éteints
   */
  signauxVus: (types) => api.post('/mon-espace/signaux/vus', types ? { types } : {}),
  /**
   * @param {number} id signal du rendez-vous
   * @param {'PRESENT'|'ABSENT'} reponse je serai là / je ne peux pas
   * @param {string} [texte] message au bureau
   * @returns {Promise<object>} le signal, répondu
   */
  repondSignal: (id, reponse, texte) =>
    api.post(`/mon-espace/signaux/${id}/reponse`, { reponse, texte }),
  /** @param {number} envoiId envoi reçu @returns {string} l'adresse de son fichier */
  adresseFichierRecu: (envoiId) => adresseApi(`/api/mon-espace/envois/${envoiId}/fichier`),
  /** @param {number} rdvId rendez-vous @returns {Promise<object[]>} les plans liés à ce rendez-vous */
  plansDuRendezVous: (rdvId) => api.get(`/mon-espace/rendez-vous/${rdvId}/plans`),
};

/**
 * Formations : l'employé demande, le bureau accepte ou refuse, l'employé signe.
 */
export const formationsApi = {
  /** @returns {Promise<object[]>} les demandes de l'employé connecté */
  mesDemandes: () => api.get('/mon-espace/formations'),
  /**
   * @param {object} demande intitule, organisme, dateDebut, dateFin (AAAA-MM-JJ), jours, prix, motif
   * @param {Blob} [fichier] programme ou inscription (photo ou PDF)
   * @returns {Promise<object>} la demande
   */
  demande: (demande, fichier) => {
    const corps = new FormData();
    Object.entries(demande || {}).forEach(([cle, valeur]) => {
      if (valeur != null && valeur !== '') corps.append(cle, String(valeur));
    });
    if (fichier) corps.append('fichier', fichier, fichier.name || 'programme');
    return api.post('/mon-espace/formations', corps);
  },
  /** @param {number} id demande acceptée @param {string} signature tracé @returns {Promise<object>} la demande, signée */
  signe: (id, signature) => api.post(`/mon-espace/formations/${id}/signature`, { signature }),
  /** @param {number} id @returns {string} la convention signée (employé) */
  adresseMaConvention: (id) => adresseApi(`/api/mon-espace/formations/${id}/convention`),
  /** @param {number} id @returns {string} la convention à lire avant de signer */
  adresseApercu: (id) => adresseApi(`/api/mon-espace/formations/${id}/convention/apercu`),
  /** @param {number} id @returns {string} le programme joint (employé) */
  adresseMonProgramme: (id) => adresseApi(`/api/mon-espace/formations/${id}/programme`),
  /** @returns {Promise<object[]>} toutes les demandes (bureau) */
  toutes: () => api.get('/equipe/formations'),
  /**
   * @param {number} id demande
   * @param {object} accord societePaie (TOUT|PARTIE|RIEN), montantSociete, joursCours
   *   (TRAVAIL|VACANCES|NON_PAYES), engagementMois, engagementTexte, texte
   * @returns {Promise<object>} la demande acceptée
   */
  accepte: (id, accord) => api.post(`/equipe/formations/${id}/accepte`, accord),
  /** @param {number} id @param {string} [texte] raison @returns {Promise<object>} la demande refusée */
  refuse: (id, texte) => api.post(`/equipe/formations/${id}/refuse`, { texte }),
  /** @param {number} id @returns {string} le programme joint (bureau) */
  adresseProgramme: (id) => adresseApi(`/api/equipe/formations/${id}/programme`),
  /** @param {number} id @returns {string} la convention signée (bureau) */
  adresseConvention: (id) => adresseApi(`/api/equipe/formations/${id}/convention`),
};

/**
 * « Envoyer à l'équipe » : patron, secrétaire, technicien.
 */
export const equipeEnvoisApi = {
  /**
   * @param {object} demande type, employeIds, equipeDuChantierId, titre, texte, siteId,
   *   lieuNom, lieuAdresse, date, heureDebut, heureFin, typeRdv, avecQui
   * @returns {Promise<object>} l'envoi, avec ses destinataires
   */
  envoie: (demande) => api.post('/equipe/envois', demande),
  /**
   * Plan ou document : le fichier part avec l'envoi.
   *
   * @param {object} demande type, employeIds, equipeDuChantierId, titre, texte, siteId, lieuNom, lieuAdresse
   * @param {Blob} fichier photo ou PDF
   * @param {string} [nomFichier] nom du fichier
   * @returns {Promise<object>} l'envoi
   */
  envoieFichier: (demande, fichier, nomFichier) => {
    const corps = new FormData();
    corps.append('fichier', fichier, nomFichier || fichier?.name || 'fichier');
    Object.entries(demande || {}).forEach(([cle, valeur]) => {
      if (valeur == null || valeur === '') return;
      if (Array.isArray(valeur)) valeur.forEach((v) => corps.append(cle, String(v)));
      else corps.append(cle, String(valeur));
    });
    return api.post('/equipe/envois/fichier', corps);
  },
  /** @returns {Promise<object[]>} les derniers envois, avec qui les a vus */
  liste: () => api.get('/equipe/envois'),
  /** @param {number} id envoi @returns {Promise<object>} l'envoi et chacun de ses destinataires */
  detail: (id) => api.get(`/equipe/envois/${id}`),
  /** @param {number} id envoi @returns {string} l'adresse de son fichier */
  adresseFichier: (id) => adresseApi(`/api/equipe/envois/${id}/fichier`),
};

/**
 * Les équipes par technicien : le patron les crée ; chaque chef lit la sienne ;
 * l'employé lit son chef d'équipe.
 */
export const equipesApi = {
  /** @returns {Promise<object[]>} id, nom, chefId, chefNom, chefRole, perimetre, employeIds, chantierIds */
  liste: () => api.get('/equipes'),
  /** @returns {Promise<{id: number, nom: string, role: string}[]>} techniciens et secrétaires */
  chefsPossibles: () => api.get('/equipes/chefs-possibles'),
  /** @param {object} equipe nom, chefId, perimetre (EQUIPE ou TOUT), employeIds, chantierIds */
  cree: (equipe) => api.post('/equipes', equipe),
  /** @param {number} id @param {object} equipe @returns {Promise<object>} l'équipe */
  metAJour: (id, equipe) => api.put(`/equipes/${id}`, equipe),
  /** @param {number} id */
  supprime: (id) => api.delete(`/equipes/${id}`),
  /** @returns {Promise<{limite: boolean, equipes: object[]}>} l'équipe que je dirige */
  moi: () => api.get('/mon-equipe'),
  /** @returns {Promise<{equipe: ?string, chefNom: ?string}>} mon équipe et mon chef (employé) */
  monChef: () => api.get('/mon-espace/equipe'),
  /** @returns {Promise<{employes: object[], chantiers: object[]}>} ce que le technicien peut prendre */
  candidats: () => api.get('/mon-equipe/candidats'),
  /** @param {{nom: string, employeIds: number[], chantierIds: number[]}} equipe mon équipe */
  regleLaMienne: (equipe) => api.put('/mon-equipe', equipe),
  /** @returns {Promise<object[]>} les équipes proposées au technicien */
  propositions: () => api.get('/mon-equipe/propositions'),
  /** @param {number} id proposition @param {number[]} [employeIds] ceux retenus */
  accepte: (id, employeIds) =>
    api.post(`/mon-equipe/propositions/${id}/accepte`, employeIds ? { employeIds } : {}),
  /** @param {number} id proposition */
  ecarte: (id) => api.post(`/mon-equipe/propositions/${id}/ecarte`, {}),
  /** @param {{chefId: number, chantierId: number, employeIds: number[], message?: string}} a */
  affecte: (a) => api.post('/equipes/affectation', a),
  /** @param {{chefId: number, chantierId: number, employeIds: number[], message?: string}} a */
  propose: (a) => api.post('/equipes/proposition', a),
};

export const employesApi = {
  /**
   * Lettres et certificats d'un employe : demission qu'il a remise, lettre de
   * resiliation, certificats. Le PDF s'ouvre sous `/employes/{id}/documents/{doc}`.
   *
   * @param {number} id employe
   * @returns {Promise<{id: number, type: string, titre: string, emisLe: string}[]>} ses documents
   */
  documents: (id) => api.get(`/employes/${id}/documents`),
  /**
   * @param {boolean} [actifsSeulement] limiter aux employés en poste
   * @returns {Promise<object[]>} les employés
   */
  liste: (actifsSeulement = true) => api.get('/employes', { actifsSeulement }),
  /**
   * @param {number} id identifiant de l'employé
   * @returns {Promise<object>} l'employé
   */
  parId: (id) => api.get(`/employes/${id}`),
  /**
   * @param {number} id identifiant de l'employé
   * @param {string} mois mois au format `AAAA-MM`
   * @param {number} [indemniteRepas] indemnité repas à ajouter au net
   * @returns {Promise<object>} la fiche de paie
   */
  ficheDePaie: (id, mois, indemniteRepas) =>
    api.get(`/employes/${id}/fiche-de-paie`, { mois, indemniteRepas }),
  /**
   * @param {object} employe données de l'employé
   * @returns {Promise<object>} l'employé créé
   */
  cree: (employe) => api.post('/employes', employe),
  /**
   * Rattache le compte de direction connecté à sa propre fiche, créée au
   * besoin : le patron, aussi salarié de sa société. La session revient
   * réémise avec la fiche.
   *
   * @returns {Promise<{fiche: object, compte: object}>} la fiche et le compte
   */
  rattacheMoi: () => api.post('/employes/moi'),
  /**
   * @param {number} id identifiant de l'employé
   * @param {object} employe nouvelles données
   * @returns {Promise<object>} l'employé mis à jour
   */
  metAJour: (id, employe) => api.put(`/employes/${id}`, employe),
  /**
   * @param {number} id identifiant de l'employé
   * @returns {Promise<null>} rien
   */
  desactive: (id) => api.delete(`/employes/${id}`),
  /**
   * Supprime définitivement un employé et son dossier (patron seul).
   *
   * @param {number} id employé
   * @returns {Promise<void>}
   */
  supprimeDefinitivement: (id) => api.delete(`/employes/${id}/definitif`),
  /**
   * Ajoute un message de la direction au fil d'un employé, sans le réécrire.
   *
   * @param {number} id employé destinataire
   * @param {string} texte contenu du message
   * @returns {Promise<object>} le message enregistré
   */
  ecrit: (id, texte) => api.post(`/employes/${id}/messages`, { texte }),
  /**
   * @param {number} id employé dont la direction ouvre le fil
   * @returns {Promise<null>} rien
   */
  marqueMessagesLus: (id) => api.post(`/employes/${id}/messages/lus`),
  /**
   * @param {number} id employé concerné
   * @returns {Promise<object>} l'état de signature de son contrat par lui
   */
  signatureContrat: (id) => api.get(`/employes/${id}/contrat/signature`),
};

/**
 * Invitation d'un employé par lien, côté direction.
 *
 * <p>Le lien et le message prêt ne sortent qu'à la création : le jeton n'est
 * pas conservé par le serveur.
 */
export const invitationsApi = {
  /**
   * @param {number} employeId fiche concernée
   * @returns {Promise<object|null>} la dernière invitation, ou `null` si la fiche n'a jamais été invitée
   */
  etat: (employeId) => api.get(`/employes/${employeId}/invitation`),
  /**
   * Invite, ou renvoie : un nouveau lien, l'ancien invalidé.
   *
   * @param {number} employeId fiche concernée
   * @param {string} [role] rôle du compte à ouvrir, ouvrier à défaut
   * @returns {Promise<object>} l'invitation, avec `lien` et `message`
   */
  invite: (employeId, role) => api.post(`/employes/${employeId}/invitation`, { role }),
  /**
   * @param {number} employeId fiche concernée
   * @returns {Promise<null>} rien ; le lien ne vaut plus
   */
  annule: (employeId) => api.delete(`/employes/${employeId}/invitation`),
};

/** Ce que l'employé fait de son invitation : aucune session n'est requise. */
/**
 * L'espace client du magasin, pour la personne connectée (rôle CLIENT) : le
 * catalogue à ses prix, sa commande, ses commandes et, pour le responsable,
 * son équipe.
 */
export const portailApi = {
  /** @returns {Promise<object>} le magasin, son entreprise et elle-même */
  moi: () => api.get('/portail/moi'),
  /** @returns {Promise<object[]>} le catalogue, aux prix du client */
  catalogue: () => api.get('/portail/catalogue'),
  /**
   * @param {number} id article
   * @returns {string} l'adresse de sa photo
   */
  image: (id) => `${import.meta.env?.VITE_API_BASE_URL ?? ''}/api/portail/articles/${id}/image`,
  /**
   * @param {{lignes: {reference: string, quantite: number}[], adresseLivraison?: string}} commande
   * @returns {Promise<object>} la commande enregistrée
   */
  commande: (commande) => api.post('/portail/commandes', commande),
  /** @returns {Promise<object[]>} les commandes, les plus récentes d'abord */
  commandes: () => api.get('/portail/commandes'),
  /** @returns {Promise<object[]>} l'équipe du client (responsable) */
  equipe: () => api.get('/portail/equipe'),
  /**
   * @param {{prenom: string, nom?: string, email?: string}} personne employé à inviter
   * @returns {Promise<object>} l'invitation, lien et message compris
   */
  invite: (personne) => api.post('/portail/equipe/invitations', personne),
  /** @param {number} id invitation à annuler */
  annuleInvitation: (id) => api.delete(`/portail/equipe/invitations/${id}`),
  /**
   * @param {number} id compte de l'employé
   * @param {boolean} actif vrai pour rouvrir
   */
  acces: (id, actif) => api.put(`/portail/equipe/${id}/acces`, null, { params: { actif } }),
};

/** Les accès d'un client à l'espace client, vus du magasin. */
export const accesClientsApi = {
  /** @param {number} clientId client du magasin */
  liste: (clientId) => api.get(`/clients/${clientId}/acces`),
  /**
   * @param {number} clientId client du magasin
   * @param {{prenom: string, nom?: string, email?: string, role?: string}} personne à inviter
   */
  invite: (clientId, personne) => api.post(`/clients/${clientId}/acces/invitations`, personne),
  /** @param {number} clientId client @param {number} id invitation */
  annule: (clientId, id) => api.delete(`/clients/${clientId}/acces/invitations/${id}`),
  /** @param {number} clientId client @param {number} id compte @param {boolean} actif rouvrir */
  acces: (clientId, id, actif) =>
    api.put(`/clients/${clientId}/acces/${id}`, null, { params: { actif } }),
};

/** L'invitation à l'espace client, sans session. */
export const accesClientPublicApi = {
  /** @param {string} jeton jeton du lien */
  page: (jeton) => api.get(`/public/acces-client/${encodeURIComponent(jeton)}`),
  /**
   * @param {string} jeton jeton du lien
   * @param {{prenom: string, nom: string, email: string, motDePasse: string}} compte le compte à créer
   */
  accepte: (jeton, compte) =>
    api.post(`/public/acces-client/${encodeURIComponent(jeton)}/accepter`, compte),
};

export const invitationsPubliquesApi = {
  /**
   * @param {string} jeton jeton porté par le lien
   * @returns {Promise<object>} la société, le nom de l'employé et le rôle
   */
  page: (jeton) => api.get(`/public/invitations/${encodeURIComponent(jeton)}`),
  /**
   * @param {string} jeton jeton porté par le lien
   * @param {{pin?: string, email?: string, motDePasse?: string}} choix adresse et mot de passe
   *   choisis, ou le code PIN tant qu'il vaut
   * @returns {Promise<object>} l'état du contrat, et le code attendu (`code`) ou le compte connecté
   */
  accepte: (jeton, choix) =>
    api.post(`/public/invitations/${encodeURIComponent(jeton)}/accepter`, choix),
};

/**
 * Déclaration annuelle des salaires.
 *
 * <p>Réservée à la direction et à la comptabilité : le serveur répond 403 aux
 * autres rôles. Les exports (CSV, XML ELM 5, PDF) sont des fichiers, servis
 * sous le même chemin et téléchargés par `api/documents.js`.
 */
export const paieApi = {
  /**
   * @param {number} annee année civile
   * @returns {Promise<object>} la déclaration, par employé et en total
   */
  declaration: (annee) => api.get(`/paie/declaration/${annee}`),
  /**
   * @param {number} annee année civile
   * @param {'csv'|'elm'|'pdf'} format export souhaité
   * @returns {string} le chemin de l'export sous `/api`
   */
  cheminExport: (annee, format) => `/paie/declaration/${annee}/${format}`,
};

/** Stock. */
export const stockApi = {
  /**
   * @param {{recherche?: string, page?: number, size?: number}} [params] filtres et pagination
   * @returns {Promise<object>} page d'articles
   */
  liste: (params) => api.get('/stock', params),
  /**
   * @returns {Promise<object[]>} tout, page après page
   */
  toutes: () => listeComplete('/stock'),
  /**
   * @returns {Promise<object[]>} les articles à réapprovisionner
   */
  alertes: () => api.get('/stock/alertes'),
  /**
   * @param {number} id identifiant de l'article
   * @returns {Promise<object>} l'article, conditionnements compris
   */
  parId: (id) => api.get(`/stock/${id}`),
  /**
   * @param {object} article données de l'article
   * @returns {Promise<object>} l'article créé
   */
  cree: (article) => api.post('/stock', article),
  /**
   * @param {number} id identifiant de l'article
   * @param {object} article nouvelles données
   * @returns {Promise<object>} l'article mis à jour
   */
  metAJour: (id, article) => api.put(`/stock/${id}`, article),
  /**
   * @param {number} id identifiant de l'article
   * @param {number} quantite quantité à ajouter (positive) ou retirer (négative)
   * @returns {Promise<object>} l'article après mouvement
   */
  mouvement: (id, quantite) =>
    api.post(`/stock/${id}/mouvement`, undefined, { params: { quantite } }),
  /**
   * @param {number} id identifiant de l'article
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/stock/${id}`),
  /**
   * Attache une photo ou un plan a un article.
   *
   * <p>Le fichier part tel quel, en piece jointe : l'encoder en base64 le
   * gonflerait d'un tiers pour rien.
   *
   * @param {number} id identifiant de l'article
   * @param {Blob} fichier image ou PDF a deposer
   * @param {'PHOTO'|'PLAN'} [typePiece] nature de la piece
   * @param {string} [nomFichier] nom du fichier tel que remis
   * @returns {Promise<object>} la piece enregistree
   */
  deposeMedia: (id, fichier, typePiece = 'PHOTO', nomFichier) => {
    const corps = new FormData();
    corps.append('fichier', fichier, nomFichier || 'photo.jpg');
    corps.append('typePiece', typePiece);
    return api.post(`/stock/${id}/medias`, corps);
  },
  /**
   * @param {number} id identifiant de l'article
   * @returns {Promise<object[]>} ses photos et ses plans
   */
  medias: (id) => api.get(`/stock/${id}/medias`),
  /**
   * @param {number} id identifiant de l'article
   * @param {number} mediaId piece a retirer
   * @returns {Promise<null>} rien
   */
  retireMedia: (id, mediaId) => api.delete(`/stock/${id}/medias/${mediaId}`),
};

/** Paramètres de la société (ressource singleton). */
export const societeApi = {
  /**
   * @returns {Promise<object|null>} les paramètres, ou `null` si la société n'est pas configurée
   */
  parametres: () => api.get('/societe'),
  /**
   * @param {object} societe paramètres à enregistrer
   * @returns {Promise<object>} les paramètres enregistrés
   */
  enregistre: (societe) => api.put('/societe', societe),
  /**
   * Lit le QR code du bulletin de versement remis par la banque.
   *
   * @param {string} image le bulletin, en adresse data:
   * @returns {Promise<{iban: string, nom: string, adresse: string, npa: string, pays: string, typeReference: string}>}
   *   le compte et le titulaire ; rejetée si le bulletin n'a pas de QR-facture lisible
   */
  lisBulletin: (image) => api.post('/societe/bulletin-banque/lecture', { image }),
  /**
   * @returns {Promise<object>} la formule, l'état de l'essai et la grille (patron)
   */
  abonnement: () => api.get('/societe/abonnement'),
  /**
   * @param {object} choix formule, périodicité et options
   * @returns {Promise<object>} l'abonnement après le choix
   */
  choisitAbonnement: (choix) => api.put('/societe/abonnement', choix),
  /**
   * @param {{secteur: string, formule: string, periodicite: string, packIa: boolean}} choix
   *        module, formule et Pack IA choisis à la création du compte
   * @returns {Promise<object>} la fiche de la société après le choix
   */
  inscritAbonnement: (choix) => api.post('/societe/abonnement/inscription', choix),
  /**
   * @param {{secteur: string, formule?: string}} changement nouveau module (patron)
   * @returns {Promise<object>} la fiche de la société dans son nouveau module
   */
  changeModule: (changement) => api.put('/societe/abonnement/module', changement),
};

/** Chantiers avec l'intégralité de leur suivi. */
export const chantiersDetailApi = {
  /**
   * @returns {Promise<object[]>} les chantiers et toutes leurs collections
   */
  liste: () => api.get('/chantiers/detail'),
  /**
   * @param {number} id identifiant du chantier
   * @returns {Promise<object>} le chantier et son suivi
   */
  parId: (id) => api.get(`/chantiers/${id}/detail`),
  /**
   * @param {object} chantier chantier et collections associées
   * @returns {Promise<object>} le chantier créé
   */
  cree: (chantier) => api.post('/chantiers/detail', chantier),
  /**
   * @param {number} id identifiant du chantier
   * @param {object} chantier chantier et collections associées
   * @returns {Promise<object>} le chantier mis à jour
   */
  metAJour: (id, chantier) => api.put(`/chantiers/${id}/detail`, chantier),
};

/** Demandes de congé. */
export const congesApi = {
  /**
   * @param {string} [statut] état recherché
   * @returns {Promise<object[]>} les demandes correspondantes
   */
  liste: (statut) => api.get('/conges', { statut }),
  /**
   * @param {object} conge données de la demande
   * @returns {Promise<object>} la demande créée
   */
  cree: (conge) => api.post('/conges', conge),
  /**
   * @param {number} id identifiant de la demande
   * @param {object} conge nouvelles données
   * @returns {Promise<object>} la demande mise à jour
   */
  metAJour: (id, conge) => api.put(`/conges/${id}`, conge),
  /**
   * @param {number} id identifiant de la demande
   * @returns {Promise<object>} la demande approuvée
   */
  approuve: (id) => api.post(`/conges/${id}/approbation`),
  /**
   * @param {number} id identifiant de la demande
   * @returns {Promise<object>} la demande refusée
   */
  refuse: (id) => api.post(`/conges/${id}/refus`),
  /**
   * @param {number} id identifiant de la demande
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/conges/${id}`),
  /**
   * @param {number} id identifiant de la demande
   * @param {string} debut premier jour propose (AAAA-MM-JJ)
   * @param {string} fin dernier jour propose (AAAA-MM-JJ)
   * @returns {Promise<object>} la demande, a « Date proposee »
   */
  propose: (id, debut, fin) => api.post(`/conges/${id}/proposition`, { debut, fin }),
  /**
   * @param {number} id identifiant de la demande
   * @param {boolean} accepte vrai pour accepter la date proposee
   * @returns {Promise<object>} la demande mise a jour
   */
  repond: (id, accepte) => api.post(`/conges/${id}/reponse`, { accepte }),
  /**
   * @param {number} id identifiant de la demande
   * @param {Blob} fichier photo ou PDF du certificat medical
   * @param {string} [nom] nom du fichier d'origine
   * @returns {Promise<object>} la demande, certificat joint
   */
  joindreCertificat: (id, fichier, nom) => {
    const corps = new FormData();
    corps.append('fichier', fichier, nom || fichier.name || 'certificat');
    return api.post(`/conges/${id}/certificat`, corps);
  },
  /**
   * @param {number} id identifiant de la demande
   * @returns {string} adresse du certificat, servi a l'employe et a la direction seulement
   */
  adresseCertificat: (id) => adresseApi(`/api/conges/${id}/certificat`),
};

/** Fichiers des pièces du dossier d'un employé, ouverts à la direction seule. */
export const dossierApi = {
  /**
   * @param {number} employeId employe concerne
   * @param {number} pieceId piece du dossier
   * @param {Blob} fichier scan ou photo
   * @param {string} [nom] nom du fichier d'origine
   * @returns {Promise<object>} la piece, fichier joint
   */
  joindre: (employeId, pieceId, fichier, nom) => {
    const corps = new FormData();
    corps.append('fichier', fichier, nom || fichier.name || 'piece');
    return api.post(`/employes/${employeId}/documents/${pieceId}/fichier`, corps);
  },
  /**
   * @param {number} employeId employe concerne
   * @param {number} pieceId piece du dossier
   * @returns {Promise<object>} la piece, sans fichier
   */
  retire: (employeId, pieceId) => api.delete(`/employes/${employeId}/documents/${pieceId}/fichier`),
  /**
   * @param {number} employeId employe concerne
   * @param {number} pieceId piece du dossier
   * @returns {string} adresse du fichier
   */
  adresse: (employeId, pieceId) =>
    adresseApi(`/api/employes/${employeId}/documents/${pieceId}/fichier`),
};

/** Dépenses. */
export const depensesApi = {
  /**
   * @param {{debut?: string, fin?: string}} [params] bornes de période
   * @returns {Promise<object[]>} les dépenses
   */
  liste: (params) => api.get('/depenses', params),
  /**
   * @param {object} depense données de la dépense
   * @returns {Promise<object>} la dépense créée
   */
  cree: (depense) => api.post('/depenses', depense),
  /**
   * @param {number} id identifiant de la dépense
   * @param {object} depense nouvelles données
   * @returns {Promise<object>} la dépense mise à jour
   */
  metAJour: (id, depense) => api.put(`/depenses/${id}`, depense),
  /**
   * @param {number} id identifiant de la dépense
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/depenses/${id}`),
  /**
   * Arrête une dépense qui revient : le serveur n'en note plus les paiements
   * suivants ; ceux déjà notés restent.
   *
   * @param {string} serie identifiant de la série
   * @returns {Promise<null>} rien
   */
  arreteSerie: (serie) => api.put(`/depenses/series/${encodeURIComponent(serie)}/arret`),
  /**
   * Garde la photo ou le PDF d'origine du justificatif, sans réduction.
   *
   * @param {Blob} fichier fichier tel que pris ou choisi
   * @param {string} [nom] nom du fichier
   * @returns {Promise<{id: number}>} l'identifiant à poser sur la dépense
   */
  envoieOriginal: (fichier, nom) => {
    const corps = new FormData();
    corps.append('fichier', fichier, nom || fichier.name || 'justificatif');
    return api.post('/depenses/originaux', corps);
  },
  /**
   * @param {number} id justificatif d'origine
   * @returns {string} adresse du fichier, tel que déposé
   */
  adresseOriginal: (id) => adresseApi(`/api/depenses/originaux/${id}`),
};

/** Position TVA calculée par le serveur. */
export const tvaApi = {
  /**
   * @param {string} debut premier jour, AAAA-MM-JJ
   * @param {string} fin dernier jour, AAAA-MM-JJ
   * @returns {Promise<object>} TVA facturée, récupérable et solde
   */
  position: (debut, fin) => api.get('/tva/position', { debut, fin }),
};

/** Ventes comptoir. */
export const ventesApi = {
  /** @returns {Promise<object[]>} les ventes */
  liste: () => api.get('/ventes'),
  /**
   * @param {object} vente données de la vente
   * @returns {Promise<object>} la vente créée
   */
  cree: (vente) => api.post('/ventes', vente),
  /**
   * @param {number} id identifiant de la vente
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/ventes/${id}`),
};

/** Commandes clients. */
export const commandesApi = {
  /** @returns {Promise<object[]>} les commandes */
  liste: () => api.get('/commandes'),
  /**
   * @param {object} commande données de la commande
   * @returns {Promise<object>} la commande créée
   */
  cree: (commande) => api.post('/commandes', commande),
  /**
   * @param {number} id identifiant de la commande
   * @param {object} commande nouvelles données
   * @returns {Promise<object>} la commande mise à jour
   */
  metAJour: (id, commande) => api.put(`/commandes/${id}`, commande),
  /**
   * @param {number} id identifiant de la commande
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/commandes/${id}`),
};

/** Bons de livraison. */
export const livraisonsApi = {
  /** @returns {Promise<object[]>} les bons de livraison */
  liste: () => api.get('/livraisons'),
  /**
   * @param {object} bon données du bon
   * @returns {Promise<object>} le bon créé
   */
  cree: (bon) => api.post('/livraisons', bon),
  /**
   * @param {number} id identifiant du bon
   * @param {object} bon nouvelles données
   * @returns {Promise<object>} le bon mis à jour
   */
  metAJour: (id, bon) => api.put(`/livraisons/${id}`, bon),
  /**
   * @param {number} id identifiant du bon
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/livraisons/${id}`),
};

/** Rendez-vous. */
export const rendezVousApi = {
  /** @returns {Promise<object[]>} les rendez-vous */
  liste: () => api.get('/rendez-vous'),
  /**
   * @param {object} rdv données du rendez-vous
   * @returns {Promise<object>} le rendez-vous créé
   */
  cree: (rdv) => api.post('/rendez-vous', rdv),
  /**
   * @param {number} id identifiant du rendez-vous
   * @param {object} rdv nouvelles données
   * @returns {Promise<object>} le rendez-vous mis à jour
   */
  metAJour: (id, rdv) => api.put(`/rendez-vous/${id}`, rdv),
  /**
   * @param {number} id identifiant du rendez-vous
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/rendez-vous/${id}`),
};

/** Catalogue de prestations. */
export const catalogueApi = {
  /**
   * @param {string} [metier] corps de métier recherché
   * @returns {Promise<object[]>} les prestations
   */
  liste: (metier) => api.get('/catalogue', { metier }),
  /**
   * @param {object} prestation données de la prestation
   * @returns {Promise<object>} la prestation créée
   */
  cree: (prestation) => api.post('/catalogue', prestation),
  /**
   * @param {number} id identifiant de la prestation
   * @param {object} prestation nouvelles données
   * @returns {Promise<object>} la prestation mise à jour
   */
  metAJour: (id, prestation) => api.put(`/catalogue/${id}`, prestation),
  /**
   * @param {number} id identifiant de la prestation
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/catalogue/${id}`),
};

/** Demandes de lien client en cours, par pièce. */
const liensEnCours = new Map();

/**
 * Liens par lesquels un client consulte sa piece sans compte : son devis, sa
 * facture, le suivi de sa livraison, le portail de son chantier. L'entreprise
 * les cree et les revoque ici.
 */
export const liensPublicsApi = {
  /**
   * Redemander le lien d'une piece rend le meme : celui deja envoye au client
   * reste valable.
   *
   * @param {'devis'|'facture'|'livraison'|'chantier'|'commande'} type type de la piece ;
   *        « commande » designe un client, dont le lien ouvre son catalogue a commander
   * @param {number} id identifiant de la piece
   * @returns {Promise<{url: string, consultations: number, premiereConsultation: ?string}>}
   *          le lien a remettre au client, et ce qu'on sait de ses consultations
   */
  pour: (type, id) => {
    // Deux écrans ouverts ensemble demandaient le même lien en même temps :
    // le second se voyait refuser (409). Ils partagent la même demande.
    const cle = type + '/' + id;
    if (!liensEnCours.has(cle)) {
      liensEnCours.set(
        cle,
        api.post(`/liens-publics/${type}/${id}`).finally(() => liensEnCours.delete(cle)),
      );
    }
    return liensEnCours.get(cle);
  },
  /**
   * Lien en vigueur, sans en creer : l'onglet Portail d'un chantier dit
   * « desactive » sans rouvrir le lien par le fait de le demander.
   *
   * @param {'devis'|'facture'|'livraison'|'chantier'} type type de la piece
   * @param {number} id identifiant de la piece
   * @returns {Promise<{url: string, consultations: number, premiereConsultation: ?string}>}
   *          le lien actif ; la promesse echoue (404) s'il n'y en a pas
   */
  existant: (type, id) => api.get(`/liens-publics/${type}/${id}`),
  /**
   * Les ouvertures d'une offre ou d'une facture par son client.
   *
   * @param {'devis'|'facture'} type type de la piece
   * @param {number} id identifiant de la piece
   * @returns {Promise<{nombre: number, instants: string[]}>} combien, et quand
   *          (la plus recente en tete)
   */
  ouvertures: (type, id) => api.get(`/liens-publics/${type}/${id}/consultations`),
  /**
   * @param {'devis'|'facture'|'livraison'|'chantier'} type type de la piece
   * @param {number} id identifiant de la piece
   * @returns {Promise<void>} le lien cesse aussitot de s'ouvrir
   */
  revoque: (type, id) => api.delete(`/liens-publics/${type}/${id}`),
};

/**
 * Ouvertures des offres et factures par les clients, que la cloche signale.
 */
export const consultationsApi = {
  /**
   * @returns {Promise<{id: number, type: 'DEVIS'|'FACTURE', referenceId: number, numero: string,
   *   client: string, consulteLe: string, lu: boolean}[]>} les dernières, la plus récente en tête
   */
  liste: () => api.get('/liens-publics/consultations'),
  /** @returns {Promise<void>} toutes marquées vues : la cloche s'éteint */
  lues: () => api.post('/liens-publics/consultations/lues'),
};

/** Ce qu'un client lit par son lien : aucune session n'est requise. */
/** L'en-tête du repère de l'appareil, quand le navigateur en garde un. */
function enTetesVisiteur() {
  const repere = repereVisiteur();
  return repere ? { 'X-Visiteur': repere } : {};
}

export const pagesPubliquesApi = {
  /**
   * @param {string} jeton jeton porte par le lien
   * @param {string} [transmis] code d'une transmission faite depuis la page
   * @param {string} [via] « pdf » quand la page s'ouvre par le bouton du PDF
   * @returns {Promise<object>} la piece, telle que le client la voit
   */
  page: (jeton, transmis, via) => {
    // Le repère de l'appareil : l'entreprise voit si le lien a été transmis ;
    // et, arrivé par un lien transmis depuis la page (« ?de=… »), à qui.
    const headers = enTetesVisiteur();
    if (transmis && /^[A-Za-z0-9]{16,40}$/.test(transmis)) headers['X-Transmis'] = transmis;
    if (via === 'pdf') headers['X-Via'] = 'pdf';
    return api.get(
      `/public/pages/${encodeURIComponent(jeton)}`,
      undefined,
      Object.keys(headers).length ? { headers } : undefined,
    );
  },
  /**
   * La personne qui ouvre la page : son nom, ou s'il faut le lui demander.
   *
   * @param {string} jeton jeton porte par le lien
   * @returns {Promise<{demandeNom: boolean, nom: ?string}>} la personne
   */
  visiteur: (jeton) =>
    api.get(`/public/pages/${encodeURIComponent(jeton)}/visiteur`, undefined, {
      headers: enTetesVisiteur(),
    }),
  /**
   * La personne qui ouvre la page donne son nom.
   *
   * @param {string} jeton jeton porte par le lien
   * @param {string} nom son nom
   * @returns {Promise<{demandeNom: boolean, nom: string}>} la personne, nommée
   */
  nomme: (jeton, nom) =>
    api.post(
      `/public/pages/${encodeURIComponent(jeton)}/nom`,
      { nom },
      { headers: enTetesVisiteur() },
    ),
  /**
   * Le client a fait une capture d'écran de sa pièce, dans l'application
   * SwissTech (iPhone ; Android 14 et plus) : le patron en est prévenu.
   *
   * @param {string} jeton jeton porté par le lien
   * @returns {Promise<void>} rien
   */
  capture: (jeton) =>
    api.post(
      `/public/pages/${encodeURIComponent(jeton)}/capture`,
      {},
      { headers: enTetesVisiteur() },
    ),
  /**
   * Transmet la pièce à une autre personne, par e-mail.
   *
   * @param {string} jeton jeton porte par le lien
   * @param {{nom: string, email: string, deLaPart?: string}} transmission à qui, de la part de qui
   * @returns {Promise<{nom: string, email: string}>} à qui elle est partie
   */
  transmet: (jeton, transmission) =>
    api.post(`/public/pages/${encodeURIComponent(jeton)}/transmettre`, transmission, {
      headers: enTetesVisiteur(),
    }),
  /**
   * Le client accepte son offre : il donne son nom et signe.
   *
   * @param {string} jeton jeton porte par le lien
   * @param {{signataire: string, signature: string}} acceptation nom et trace de la signature
   * @returns {Promise<object>} la page de l'offre, acceptee
   */
  accepte: (jeton, acceptation) =>
    api.post(`/public/pages/${encodeURIComponent(jeton)}/accepter`, acceptation),
  /**
   * Le client refuse son offre, avec un motif s'il veut bien le dire.
   *
   * @param {string} jeton jeton du lien
   * @param {{motif?: string}} refus motif, facultatif
   * @returns {Promise<object>} la page de l'offre, refusée
   */
  refuse: (jeton, refus) =>
    api.post(`/public/pages/${encodeURIComponent(jeton)}/refuser`, refus ?? {}),
  /**
   * Le client passe commande depuis son lien de commande : references et
   * quantites ; les prix sont relus au serveur.
   *
   * @param {string} jeton jeton porte par le lien
   * @param {{adresseLivraison: string, lignes: {reference: string, quantite: number}[]}} demande
   *        ce qu'il commande
   * @returns {Promise<{numero: string, date: string, total: number, devise: string}>}
   *          la commande enregistree
   */
  commande: (jeton, demande) =>
    api.post(`/public/pages/${encodeURIComponent(jeton)}/commander`, demande),
  /**
   * @param {string} jeton jeton porte par le lien
   * @returns {string} adresse de la piece officielle, en PDF
   */
  adresseDuDocument: (jeton) => {
    const repere = repereVisiteur();
    return (
      `/api/public/pages/${encodeURIComponent(jeton)}/document` +
      (repere ? `?v=${encodeURIComponent(repere)}` : '')
    );
  },
  /**
   * Le PDF ouvert par le bouton de l'e-mail d'envoi : le bureau voit
   * « PDF ouvert », et non « PDF téléchargé » depuis la page.
   *
   * @param {string} jeton jeton porte par le lien
   * @returns {string} l'adresse du PDF
   */
  adresseDuPdfCourriel: (jeton) => {
    const repere = repereVisiteur();
    return (
      `/api/public/pages/${encodeURIComponent(jeton)}/document?via=courriel` +
      (repere ? `&v=${encodeURIComponent(repere)}` : '')
    );
  },
  /**
   * Les photos de l'offre ou de la facture, dans l'ordre du PDF.
   *
   * @param {string} jeton jeton porte par le lien
   * @returns {Promise<{nomPiece: string, numero: string, societe: string, langue: string,
   *   photos: {legende: ?string, nature: ?string}[]}>} la pièce et ses photos
   */
  images: (jeton) => api.get(`/public/pages/${encodeURIComponent(jeton)}/images`),
  /**
   * @param {string} jeton jeton porte par le lien
   * @param {number} numero rang de la photo dans le PDF, à partir de 1
   * @returns {string} l'adresse de la photo, en taille réelle
   */
  adresseDeLImage: (jeton, numero) =>
    `/api/public/pages/${encodeURIComponent(jeton)}/images/${Number(numero) || 1}`,
  /**
   * La pièce en page web, le même document que le PDF à la taille de l'écran.
   *
   * @param {string} jeton jeton porte par le lien
   * @returns {Promise<string|null>} la page HTML, ou rien si elle n'est pas disponible
   */
  feuille: (jeton) =>
    fetch(`/api/public/pages/${encodeURIComponent(jeton)}/feuille`, {
      headers: { Accept: 'text/html' },
    })
      .then((r) => (r.ok ? r.text() : null))
      .catch(() => null),
  /**
   * Les photos du portail d'un chantier s'ouvrent par le meme jeton : la page
   * rend leur adresse relative a l'API, et l'ecran la complete.
   *
   * @param {string} image adresse rendue par la page, relative a l'API
   * @returns {string} l'adresse a laquelle le navigateur charge l'image
   */
  adresseDeLaPhoto: (image) => adresseApi(image),
  /**
   * Les attestations de l'entreprise jointes à une offre s'ouvrent par le
   * même jeton : la page rend leur adresse relative à l'API.
   *
   * @param {string} adresse adresse rendue par la page
   * @returns {string} l'adresse que le navigateur ouvre
   */
  adresseDuDocumentJoint: (adresse) => adresseApi(adresse),
};

/**
 * Donnees personnelles, au sens de la LPD : le droit d'acces et le droit a
 * l'effacement. Chacun emporte les siennes ; l'etat et l'effacement d'un
 * employe ou d'un client sont reserves a la direction.
 */
export const donneesPersonnellesApi = {
  /**
   * @returns {Promise<object>} tout ce que l'entreprise detient sur la personne connectee
   */
  moi: () => api.get('/donnees-personnelles/moi'),
  /**
   * @param {number} id identifiant de l'employe
   * @returns {Promise<object>} l'etat de ses donnees, pret a lui etre remis
   */
  employe: (id) => api.get(`/donnees-personnelles/employes/${id}`),
  /**
   * @param {number} id identifiant du client
   * @returns {Promise<object>} l'etat de ses donnees, pret a lui etre remis
   */
  client: (id) => api.get(`/donnees-personnelles/clients/${id}`),
  /**
   * Rend la fiche anonyme ; les pieces que la loi impose de garder restent.
   *
   * @param {number} id identifiant de l'employe
   * @returns {Promise<object>} le compte rendu : champs effaces, pieces retenues et pourquoi
   */
  effaceEmploye: (id) => api.delete(`/donnees-personnelles/employes/${id}`),
  /**
   * @param {number} id identifiant du client
   * @returns {Promise<object>} le compte rendu : champs effaces, pieces retenues et pourquoi
   */
  effaceClient: (id) => api.delete(`/donnees-personnelles/clients/${id}`),
};

/** Feuilles de régie. */
export const feuillesApi = {
  /**
   * @param {number} [employeeId] employé concerné
   * @returns {Promise<object[]>} les feuilles de régie
   */
  liste: (employeeId) => api.get('/feuilles', { employeeId }),
  /**
   * @param {object} feuille données de la feuille
   * @returns {Promise<object>} la feuille créée
   */
  cree: (feuille) => api.post('/feuilles', feuille),
  /**
   * @param {number} id identifiant de la feuille
   * @param {object} feuille nouvelles données
   * @returns {Promise<object>} la feuille mise à jour
   */
  metAJour: (id, feuille) => api.put(`/feuilles/${id}`, feuille),
  /**
   * @param {number} id identifiant de la feuille
   * @returns {Promise<null>} rien
   */
  supprime: (id) => api.delete(`/feuilles/${id}`),
};

/** Journal des connexions. */
export const journalApi = {
  /**
   * @param {number} [limite] nombre maximal d'entrées
   * @returns {Promise<object[]>} les entrées du journal
   */
  liste: (limite) => api.get('/journal-connexions', { limite }),
  /**
   * @param {object} connexion nom et rôle de la personne connectée
   * @returns {Promise<object>} l'entrée créée
   */
  consigne: (connexion) => api.post('/journal-connexions', connexion),
  /**
   * @returns {Promise<null>} rien
   */
  vide: () => api.delete('/journal-connexions'),
};

/** Photos de chantier. */
export const photosApi = {
  /**
   * Dépose une photo sur un chantier.
   *
   * <p>Le fichier part tel quel, en pièce jointe : l'encoder en base64 le
   * gonflerait d'un tiers pour rien.
   *
   * @param {number} chantierId chantier concerné
   * @param {Blob} fichier image à déposer
   * @param {{categorie?: string, libelle?: string, auteur?: string, poste?: string,
   *          note?: string, partageClient?: boolean}} [description] légende, catégorie,
   *        poste de travail, note et partage au client
   * @returns {Promise<object>} la photo enregistrée
   */
  depose: (chantierId, fichier, description = {}) => {
    const corps = new FormData();
    corps.append('fichier', fichier, description.nomFichier || 'photo.jpg');
    // Le poste et la note partent avec la photo : sans eux, toutes retombaient
    // sous « General » au rechargement et la carte disait « Photo ».
    for (const cle of ['categorie', 'libelle', 'auteur', 'poste', 'note']) {
      if (description[cle]) corps.append(cle, description[cle]);
    }
    // L'ouvrier destine sa photo au client des la prise de vue : le drapeau
    // part avec le fichier, sinon elle restait interne jusqu'au bureau.
    if (typeof description.partageClient === 'boolean') {
      corps.append('partageClient', String(description.partageClient));
    }
    return api.post(`/chantiers/${chantierId}/photos`, corps);
  },
  /**
   * @param {number} chantierId chantier concerné
   * @param {number} photoId photo à retirer
   * @returns {Promise<null>} rien
   */
  supprime: (chantierId, photoId) => api.delete(`/chantiers/${chantierId}/photos/${photoId}`),
};

/** Plans de chantier. */
export const plansApi = {
  /**
   * Dépose un plan sur un chantier.
   *
   * <p>Un plan est un fichier, pas une ligne de texte : il part en pièce
   * jointe, comme la photo, et le chantier n'en garde que l'adresse.
   *
   * @param {number} chantierId chantier concerné
   * @param {Blob} fichier image ou PDF à déposer
   * @param {{nom?: string, emoji?: string, nomFichier?: string}} [description] nom affiché et pictogramme
   * @returns {Promise<object>} le plan enregistré
   */
  depose: (chantierId, fichier, description = {}) => {
    const corps = new FormData();
    corps.append('fichier', fichier, description.nomFichier || 'plan.jpg');
    for (const cle of ['nom', 'emoji']) {
      if (description[cle]) corps.append(cle, description[cle]);
    }
    return api.post(`/chantiers/${chantierId}/plans`, corps);
  },
  /**
   * @param {number} chantierId chantier concerné
   * @param {number} planId plan à retirer
   * @returns {Promise<null>} rien
   */
  supprime: (chantierId, planId) => api.delete(`/chantiers/${chantierId}/plans/${planId}`),
};

/**
 * Photos jointes aux devis, factures, bons de livraison et decharges.
 *
 * <p>Celle d'un bon de livraison est une preuve : c'est ce qui a ete depose
 * sur le chantier, au moment de la remise. Celles d'une decharge figurent sur
 * son PDF, et y sont figees avec elle.
 */
export const piecesApi = {
  /**
   * @param {'DEVIS'|'FACTURE'|'LIVRAISON'|'DECHARGE'|'STOCK'|'CHANTIER'} genre genre du document
   * @param {number} documentId document concerne
   * @returns {Promise<object[]>} ses pieces jointes
   */
  liste: (genre, documentId) => api.get(`/pieces/${genre}/${documentId}`),
  /**
   * @param {'DEVIS'|'FACTURE'} genre offre ou facture
   * @param {number} documentId document concerné
   * @returns {Promise<object[]>} les photos et plans de ses lignes, chacun avec sa ligne
   */
  lignes: (genre, documentId) => api.get(`/pieces/${genre}/${documentId}/lignes`),
  /**
   * Les pièces de plusieurs offres et factures d'un coup : la liste d'un
   * client ou d'un chantier (marchandise, sous-traitance, photos).
   *
   * @param {{devis?: number[], factures?: number[]}} ids documents concernés
   * @returns {Promise<{genre: string, documentId: number, piece: object}[]>} leurs pièces
   */
  desDocuments: ({ devis = [], factures = [] } = {}) =>
    api.get('/pieces/documents', {
      devis: devis.length ? devis.join(',') : undefined,
      factures: factures.length ? factures.join(',') : undefined,
    }),
  /**
   * La marchandise et la sous-traitance jointes à toutes les offres et
   * factures : les dépenses de l'accueil et de la comptabilité.
   *
   * @returns {Promise<{genre: string, documentId: number, piece: object}[]>} les pièces chiffrées
   */
  achats: () => api.get('/pieces/achats'),
  /**
   * @param {'DEVIS'|'FACTURE'|'LIVRAISON'|'DECHARGE'|'STOCK'|'CHANTIER'} genre genre du document
   * @param {number} documentId document concerne
   * @param {Blob} fichier photo a joindre
   * @param {string} [libelle] ce que la piece montre
   * @param {{fournisseur?: string, reference?: string, montant?: number, categorie?: string}} [chiffres]
   *        ce qu'une facture jointe porte en plus d'une photo
   * @returns {Promise<object>} la piece enregistree
   */
  depose: (genre, documentId, fichier, libelle, chiffres = {}) => {
    const corps = new FormData();
    // Le fichier est facultatif : un artisan note le fournisseur et le montant
    // de tete, la photo du bon suivra -- ou ne suivra pas.
    if (fichier) corps.append('fichier', fichier, fichier.name || 'photo.jpg');
    if (libelle) corps.append('libelle', libelle);
    for (const cle of ['fournisseur', 'reference', 'montant', 'categorie', 'montantTva', 'date']) {
      if (chiffres[cle] !== undefined && chiffres[cle] !== null && chiffres[cle] !== '') {
        corps.append(cle, chiffres[cle]);
      }
    }
    return api.post(`/pieces/${genre}/${documentId}`, corps);
  },
  /**
   * @param {number} id piece a retirer
   * @returns {Promise<null>} rien
   */
  retire: (id) => api.delete(`/pieces/${id}`),
  /**
   * Note le jour où une facture jointe a été payée, ou l'efface.
   *
   * @param {number} id pièce
   * @param {string} payeLe jour, « JJ.MM.AAAA » ou ISO ; vide l'efface
   * @param {string} [payePar] « banque », « carte » ou « especes »
   * @returns {Promise<object>} la pièce à jour
   */
  paiement: (id, payeLe, payePar) =>
    api.put(`/pieces/${id}/paiement`, {
      payeLe: dateVersIso(payeLe),
      payePar: payeLe && payePar ? String(payePar).toUpperCase() : null,
    }),
  /**
   * @param {number} devisId devis concerne
   * @param {string} cle cle stable de la ligne
   * @param {Blob} fichier photo ou plan
   * @param {'PHOTO'|'PLAN'} typePiece nature de la piece
   * @param {string} [nom] nom du fichier d'origine
   * @param {'DEVIS'|'FACTURE'} [genre] offre ou facture
   * @returns {Promise<object>} la piece enregistree
   */
  deposeSurLigne: (devisId, cle, fichier, typePiece, nom, genre = 'DEVIS') => {
    const corps = new FormData();
    corps.append('fichier', fichier, nom || fichier.name || 'photo.jpg');
    corps.append('typePiece', typePiece);
    return api.post(`/pieces/${genre}/${devisId}/lignes/${encodeURIComponent(cle)}`, corps);
  },
};

/**
 * Reprise d'un ancien logiciel (direction et comptabilité ; 403 aux autres).
 *
 * <p>Un fichier s'importe en entier ou pas du tout : l'aperçu juge les lignes
 * sans rien écrire, l'import ne part que s'il ne reste aucune erreur.
 */
export const repriseApi = {
  /**
   * @param {'clients'|'devis'|'factures'|'depenses'} type ce que contient le fichier
   * @param {string} fichier nom du fichier
   * @param {Object<string, string>[]} lignes lignes lues, « en-tête → valeur »
   * @param {boolean} simulation vrai pour l'aperçu
   * @returns {Promise<object>} le compte rendu (lues, importées, ignorées, erreurs)
   */
  envoie: (type, fichier, lignes, simulation) =>
    api.post(`/reprise/${type}`, { fichier, lignes }, { params: { simulation } }),
  /**
   * Texte d'une ancienne facture ou offre en PDF, lignes du tableau d'un seul tenant.
   *
   * @param {Blob} fichier le PDF
   * @param {string} [nomFichier] nom du fichier
   * @returns {Promise<{texte: string}>} le texte, une ligne du document par ligne
   */
  texteDuPdf: (fichier, nomFichier, delaiMs = 60000) => {
    const corps = new FormData();
    corps.append('fichier', fichier, nomFichier || 'document.pdf');
    // Un serveur qui ne répond pas (mise à jour en cours, réseau coupé)
    // laissait « Lecture du PDF… » tourner sans fin : au bout d'une minute,
    // la lecture s'arrête et l'écran propose de réessayer.
    const arret = new AbortController();
    const minuterie = setTimeout(() => arret.abort(), delaiMs);
    return api
      .post('/reprise/pdf-texte', corps, { signal: arret.signal })
      .catch((e) => {
        if (e?.name === 'AbortError' || e?.reseau) {
          throw new Error(
            'SwissTech ne répond pas pour l’instant (mise à jour ou réseau). Réessayez dans une minute.',
          );
        }
        throw e;
      })
      .finally(() => clearTimeout(minuterie));
  },
};

/**
 * Pack IA : rédaction assistée, lecture de tickets, assistant.
 *
 * <p>Le serveur répond 403 si la société n'a pas l'option, 503 s'il n'a pas de
 * clé, 429 quand la part horaire de la société est épuisée et 502 quand le
 * modèle a répondu hors format : chaque écran traduit ces refus poliment.
 */
export const iaApi = {
  /**
   * @param {{genre: 'devis'|'courrier', titre?: string, mots?: string}} demande ce qu'il faut rédiger
   * @returns {Promise<{texte: string}>} la proposition
   */
  redaction: (demande) => api.post('/ia/redaction', demande),
  /**
   * @param {{contenu: string, typeMedia?: string, nomFichier?: string}} demande le ticket, encodé
   * @returns {Promise<object>} ce qui a été lu, avec un score de confiance
   */
  lectureDepense: (demande) => api.post('/ia/lecture-depense', demande),
  /**
   * @param {{question: string, historique?: {role: string, contenu: string}[]}} demande la question
   * @returns {Promise<{reponse: string}>} la réponse
   */
  assistant: (demande) => api.post('/ia/assistant', demande),
  /**
   * @param {{fichiers: {contenu: string, typeMedia?: string, nomFichier?: string}[],
   *          filtre: 'metier'|'tout'|'choix', metiers?: string[], autre?: string}} demande
   *        la soumission, encodée, et le filtre de métier
   * @returns {Promise<object>} postes rapprochés du catalogue et rapport de vigilance
   */
  lectureSoumission: (demande) => api.post('/ia/soumissions/lecture', demande),
  /**
   * @param {{fichiers: {contenu: string, typeMedia?: string, nomFichier?: string}[]}} demande
   *        le catalogue fournisseur, encodé
   * @returns {Promise<object>} articles proposés, sans rien écrire
   */
  lectureCatalogue: (demande) => api.post('/ia/catalogue/lecture', demande),
  /**
   * @param {{fichiers: {contenu: string, typeMedia?: string, nomFichier?: string}[]}} demande
   *        la feuille de notes, photographiée ou scannée
   * @returns {Promise<object>} client reconnu ou à créer, lignes chiffrées depuis le catalogue
   */
  lectureNotes: (demande) => api.post('/ia/notes/lecture', demande),
  /**
   * @param {{fichiers: {contenu: string, typeMedia?: string, nomFichier?: string}[]}} demande
   *        la facture fournisseur, photographiée ou scannée
   * @returns {Promise<object>} fournisseur, numéro, montants, IBAN et lignes lus
   */
  lectureFacture: (demande) => api.post('/ia/factures-fournisseur/lecture', demande),
  /**
   * @param {{fichiers: {contenu: string, typeMedia?: string, nomFichier?: string}[]}} demande
   *        le devis ou la facture papier à reprendre, photographié ou scanné
   * @returns {Promise<object>} genre, destinataire reconnu ou à créer, lignes au prix du document
   */
  lectureDocument: (demande) => api.post('/ia/documents/lecture', demande),
  /**
   * @param {{fournisseur?: string, prixSont: 'achat'|'vente', articles: object[]}} demande
   *        les articles validés à l'écran de vérification
   * @returns {Promise<{crees: number, misAJour: number, sansPrix: string[], images: number}>}
   *          ce que l'import a fait
   */
  importeCatalogue: (demande) => api.post('/ia/catalogue/import', demande),
  /**
   * @returns {Promise<object>} état du pack, compteur du mois et derniers appels
   */
  journal: () => api.get('/ia/journal'),
};

/** Authentification, session et comptes utilisateurs. */
/** Ce que le créateur de SwissTech voit de la plateforme. */
export const plateformeApi = {
  /** @returns {Promise<{createur: boolean}>} vrai pour le compte du créateur */
  acces: () => api.get('/plateforme/acces'),
  /**
   * @returns {Promise<Array<{le: string, email: string, telephone: ?string, nom: ?string,
   *          entreprise: ?string, resultat: 'CREE'|'REFUSEE', motif: ?string}>>}
   *          les inscriptions, la plus récente en tête
   */
  inscriptions: () => api.get('/plateforme/inscriptions'),
};

/** Connexions de l'équipe, que le patron lit dans le journal des connexions. */
export const connexionsEquipeApi = {
  /**
   * @param {number} [compte] un seul compte ; toute l'équipe sans lui
   * @returns {Promise<Array<{compteId: ?number, nom: string, role: string, connecteLe: string}>>}
   *          les connexions, la plus récente en tête
   */
  liste: (compte) => api.get('/connexions-equipe', { params: compte ? { compte } : undefined }),
};

export const authApi = {
  /**
   * @returns {Promise<object|null>} le compte connecté, ou `null` sans session
   */
  moi: () => api.get('/auth/moi'),
  /**
   * Change son propre nom : compte, fiche d'employé rattachée et bons d'achat.
   *
   * @param {string} prenom prénom
   * @param {string} nom nom de famille
   * @returns {Promise<object>} le compte renommé
   */
  renomme: (prenom, nom) => api.put('/auth/moi/nom', { prenom, nom }),
  /**
   * Sociétés auxquelles le compte connecté a accès, la courante signalée.
   *
   * @returns {Promise<object[]>} les sociétés
   */
  societes: () => api.get('/auth/societes'),
  /**
   * Ouvre une autre société du compte : la session est réémise pour elle, et
   * tout ce qui suit ne voit plus qu'elle.
   *
   * @param {number} id société à ouvrir
   * @returns {Promise<object>} le compte, désormais dans cette société
   */
  basculeSociete: (id) => api.post(`/auth/societes/${id}/bascule`),
  /**
   * @param {{nom: string, secteur: string}} demande raison sociale et secteur
   * @returns {Promise<object>} le compte, désormais dans la nouvelle société
   */
  creeSociete: (demande) => api.post('/auth/societes', demande),
  /**
   * @param {{nomEntreprise: string, nomComplet: string, email: string, motDePasse: string}} demande
   *        raison sociale et premier compte
   * @returns {Promise<object>} le compte créé
   */
  inscription: (demande) => api.post('/auth/inscription', demande),
  /**
   * @param {string} email adresse de connexion
   * @param {string} motDePasse mot de passe
   * @returns {Promise<object>} le compte connecté
   */
  connexion: (email, motDePasse) => api.post('/auth/connexion', { email, motDePasse }),
  /**
   * Mode de connexion en vigueur, lu sans session : code à six chiffres
   * obligatoire pour tous, ou encore l'entrée « Code ouvrier ».
   *
   * @returns {Promise<{codeObligatoire: boolean, codeOuvrier: boolean, sms: boolean}>} le mode
   */
  modeConnexion: () => api.get('/auth/mode-connexion'),
  /**
   * L'entreprise est désignée par son code, non par son numéro : celui-ci est
   * une suite, et se parcourait — n'importe qui obtenait ainsi la liste
   * nominative des employés de chaque entreprise servie par l'instance.
   *
   * @param {string} code code de l'entreprise, affiché par le responsable
   * @returns {Promise<object[]>} les employés pouvant se connecter par code
   */
  employesAvecPin: (code) => api.get('/auth/entreprises-pin', { code }),
  /**
   * @param {string} codeEntreprise code de l'entreprise
   * @param {number} employeeId employé désigné
   * @param {string} pin code à quatre chiffres
   * @returns {Promise<object>} le compte connecté
   */
  connexionPin: (codeEntreprise, employeeId, pin) =>
    api.post('/auth/connexion-pin', { codeEntreprise, employeeId, pin }),
  /**
   * @returns {Promise<null>} rien
   */
  deconnexion: () => api.post('/auth/deconnexion'),
  /**
   * @returns {Promise<object[]>} les comptes de l'entreprise
   */
  comptes: () => api.get('/auth/comptes'),
  /**
   * @param {number} id compte à désactiver
   * @returns {Promise<null>} rien
   */
  desactiveCompte: (id) => api.delete(`/auth/comptes/${id}`),
  /**
   * @param {number} id compte concerné
   * @param {string} role nouveau rôle
   * @returns {Promise<object>} le compte mis à jour
   */
  modifieCompte: (id, role) => api.put(`/auth/comptes/${id}`, { role }),
  /**
   * @param {string} ancien mot de passe actuel
   * @param {string} nouveau nouveau mot de passe
   * @returns {Promise<null>} rien
   */
  changeMotDePasse: (ancien, nouveau) => api.post('/auth/mot-de-passe', { ancien, nouveau }),
  /**
   * Demande un lien de réinitialisation.
   *
   * <p>Répond de la même façon que l'adresse soit connue ou non : l'interface
   * ne peut donc rien affirmer de plus que « si un compte existe, le message
   * est parti ».
   *
   * @param {string} email adresse du compte
   * @returns {Promise<null>} rien
   */
  motDePasseOublie: (email) => api.post('/auth/mot-de-passe-oublie', { email }),
  /**
   * @param {string} jeton jeton contenu dans le lien reçu
   * @param {string} nouveau nouveau mot de passe
   * @returns {Promise<null>} rien
   */
  reinitialise: (jeton, nouveau) => api.post('/auth/reinitialisation', { jeton, nouveau }),
  /**
   * Achève une connexion par le code reçu par e-mail, quand le compte l'exige.
   *
   * @param {string} defi jeton reçu avec l'état « code requis »
   * @param {string} code code à six chiffres
   * @returns {Promise<object>} le compte connecté
   */
  connexionCode: (defi, code) => api.post('/auth/connexion/code', { defi, code }),
  /**
   * @param {string} defi jeton du défi en cours
   * @returns {Promise<{codeRequis: boolean, defi: string}>} le nouveau défi
   */
  renvoieCode: (defi) => api.post('/auth/connexion/code/renvoi', { defi }),
  /**
   * Envoie le code de connexion par le moyen choisi après le mot de passe.
   *
   * @param {string} defi jeton du défi en cours
   * @param {'EMAIL'|'SMS'} canal e-mail ou SMS
   * @returns {Promise<{codeRequis: boolean, defi: string}>} le défi dont le code est parti
   */
  envoieCode: (defi, canal) => api.post('/auth/connexion/code/envoi', { defi, canal }),
  /**
   * Achève une connexion par un code de secours (téléphone perdu, e-mail
   * éteint). Chaque code ne sert qu'une fois.
   *
   * @param {string} defi jeton reçu avec l'état « code requis »
   * @param {string} code huit chiffres, « 4821-7730 »
   * @returns {Promise<object>} le compte connecté
   */
  connexionSecours: (defi, code) => api.post('/auth/connexion/secours', { defi, code }),
  /**
   * Allume ou éteint le code par e-mail ou par SMS. Le serveur refuse
   * d'éteindre le dernier moyen allumé.
   *
   * @param {'EMAIL'|'SMS'} moyen moyen à régler
   * @param {boolean} allume vrai pour allumer
   * @param {string} motDePasse mot de passe actuel
   * @returns {Promise<{etat: object, codesSecours: string[]}>} l'état, et les
   *     codes de secours tirés à l'instant (montrés une seule fois)
   */
  regleMoyen: (moyen, allume, motDePasse) =>
    api.put('/auth/2fa/moyens', { moyen, allume, motDePasse }),
  /**
   * Tire six nouveaux codes de secours ; les anciens ne marchent plus.
   *
   * @param {string} motDePasse mot de passe actuel
   * @returns {Promise<{codes: string[]}>} les codes, montrés une seule fois
   */
  refaitCodesSecours: (motDePasse) => api.post('/auth/2fa/secours', { motDePasse }),
  /**
   * Change le numéro de portable qui reçoit le code par SMS.
   *
   * @param {string} motDePasse mot de passe actuel
   * @param {string} telephone numéro, vide pour le retirer
   * @returns {Promise<object>} l'état de la double authentification
   */
  changeTelephoneCode: (motDePasse, telephone) =>
    api.put('/auth/2fa/telephone', { motDePasse, telephone }),
  /**
   * @returns {Promise<{active: boolean}>} l'état de la double authentification
   */
  etatDoubleAuth: () => api.get('/auth/2fa'),
  /**
   * @param {string} motDePasse mot de passe actuel
   * @returns {Promise<{defi: string}>} le défi dont le code confirmera l'activation
   */
  activeDoubleAuth: (motDePasse, canal) =>
    api.post('/auth/2fa/activation' + (canal ? '?canal=' + canal : ''), { motDePasse }),
  /**
   * @param {string} defi jeton du défi d'activation
   * @param {string} code code reçu par e-mail
   * @param {'EMAIL'|'SMS'} [methode] 2ᵉ sécurité choisie, qui servira à chaque connexion
   * @returns {Promise<{active: boolean}>} l'état, désormais actif
   */
  confirmeDoubleAuth: (defi, code, methode) =>
    api.post('/auth/2fa/activation/confirmation' + (methode ? '?methode=' + methode : ''), {
      defi,
      code,
    }),
  /**
   * @param {string} motDePasse mot de passe actuel
   * @returns {Promise<{active: boolean}>} l'état, désormais inactif
   */
  desactiveDoubleAuth: (motDePasse) => api.post('/auth/2fa/desactivation', { motDePasse }),
  /**
   * Commence la liaison d'une application d'authentification (Google
   * Authenticator…), contre le mot de passe.
   *
   * @param {string} motDePasse mot de passe actuel
   * @returns {Promise<{secret: string, adresse: string}>} la clé et l'adresse du code QR
   */
  relieApplication: (motDePasse) => api.post('/auth/2fa/application', { motDePasse }),
  /**
   * @param {string} code premier code affiché par l'application
   * @returns {Promise<object>} l'état, l'application reliée
   */
  confirmeApplication: (code) => api.post('/auth/2fa/application/confirmation', { code }),
  /**
   * @param {string} motDePasse mot de passe actuel
   * @returns {Promise<object>} l'état, sans application
   */
  retireApplication: (motDePasse) => api.post('/auth/2fa/application/retrait', { motDePasse }),
  /**
   * « Plus tard » : le choix de la deuxième sécurité ne revient qu'à la
   * prochaine connexion, pas à chaque ouverture de l'application.
   *
   * @returns {Promise<object>} l'état à jour
   */
  reporteChoixSecurite: () => api.post('/auth/2fa/plus-tard', {}),
  /**
   * Demande un lien de connexion sans mot de passe. Répond de même que
   * l'adresse soit connue ou non.
   *
   * @param {string} email adresse du compte
   * @returns {Promise<null>} rien
   */
  demandeLien: (email) => api.post('/auth/lien', { email }),
  /**
   * @param {string} jeton jeton contenu dans le lien reçu
   * @returns {Promise<object>} le compte connecté
   */
  ouvreLien: (jeton) => api.post('/auth/lien/ouvrir', { jeton }),
};

/** Courriers enregistrés : ils se retrouvent, et leur PDF se range au classeur. */
/**
 * Bons d'achat de matériaux, au bureau : établis pour un employé, sur un
 * chantier, sans prix, puis envoyés dans son espace.
 */
export const bonsAchatApi = {
  /** @returns {Promise<object[]>} les bons, les plus récents d'abord */
  liste: () => api.get('/bons-achat'),
  /** @param {object} b bon à établir @returns {Promise<object>} le bon numéroté */
  cree: (b) => api.post('/bons-achat', b),
  /** @param {number} id bon @param {object} b contenu @returns {Promise<object>} le bon */
  modifie: (id, b) => api.put(`/bons-achat/${id}`, b),
  /** @param {number} id bon @returns {Promise<object>} le bon, arrivé chez l'employé */
  envoie: (id) => api.post(`/bons-achat/${id}/envoi`),
  /** @param {number} id bon à supprimer */
  supprime: (id) => api.delete(`/bons-achat/${id}`),
  /** @returns {Promise<object[]>} le carnet des fournisseurs, par nom */
  fournisseurs: () => api.get('/bons-achat/fournisseurs'),
  /** @param {object} f nom, adresse, téléphone @returns {Promise<object>} le fournisseur ajouté */
  ajouteFournisseur: (f) => api.post('/bons-achat/fournisseurs', f),
  /** @param {number} id fournisseur @param {object} f contenu @returns {Promise<object>} le fournisseur */
  modifieFournisseur: (id, f) => api.put(`/bons-achat/fournisseurs/${id}`, f),
  /** @param {number} id fournisseur à retirer du carnet */
  supprimeFournisseur: (id) => api.delete(`/bons-achat/fournisseurs/${id}`),
};

export const courriersApi = {
  /** @returns {Promise<object[]>} les courriers, les plus récents d'abord */
  liste: () => api.get('/courriers'),
  /** @param {object} c courrier à enregistrer @returns {Promise<object>} le courrier numéroté */
  cree: (c) => api.post('/courriers', c),
  /** @param {number} id courrier @param {object} c contenu @returns {Promise<object>} le courrier */
  metAJour: (id, c) => api.put(`/courriers/${id}`, c),
  /** @param {number} id courrier à supprimer */
  supprime: (id) => api.delete(`/courriers/${id}`),
  /** @param {number} id courrier @param {string} email destinataire @returns {Promise<object>} le journal */
  envoie: (id, email) => api.post(`/courriers/${id}/email`, { email }),
};

/** Signature personnelle du compte : dessinée une fois, posée d'un geste. */
export const maSignatureApi = {
  /** @returns {Promise<{signature: string|null}>} la signature enregistrée */
  lit: () => api.get('/ma-signature'),
  /** @param {string|null} signature image PNG encodée, ou null pour l'effacer */
  enregistre: (signature) => api.put('/ma-signature', { signature: signature || null }),
};

/** Envois faits par le serveur : rappels automatiques, journal, envoi d'une pièce. */
export const envoisApi = {
  /**
   * @returns {Promise<object>} les réglages d'envoi automatique de la société
   */
  reglages: () => api.get('/envois/reglages'),
  /**
   * @param {object} reglages réglages voulus
   * @returns {Promise<object>} les réglages enregistrés
   */
  enregistreReglages: (reglages) => api.put('/envois/reglages', reglages),
  /**
   * @returns {Promise<object[]>} un aperçu du texte de chaque envoi, au nom de la société
   */
  apercu: () => api.get('/envois/apercu'),
  /**
   * @param {{page?: number, size?: number}} [params] pagination
   * @returns {Promise<object>} la page du journal, du plus récent au plus ancien
   */
  journal: (params) => api.get('/envois', params),
  /**
   * Efface du journal un envoi fait depuis l'écran (pas un rappel automatique).
   *
   * @param {number} id ligne du journal
   * @returns {Promise<void>}
   */
  effaceDuJournal: (id) => api.delete(`/envois/journal/${id}`),
  /**
   * Note au journal une offre ou une facture partagée par le téléphone.
   *
   * @param {'DEVIS'|'FACTURE'} type sorte de pièce
   * @param {number} id pièce partagée
   * @param {'WHATSAPP'|'SMS'|'EMAIL'|'PARTAGE'} canal application choisie
   * @returns {Promise<object>} la ligne du journal
   */
  noteUnPartage: (type, id, canal) => api.post('/envois/partage', { type, id, canal }),
  /**
   * Passe le traitement quotidien pour la société, sans attendre l'heure réglée.
   *
   * @returns {Promise<{jour: string, envoyes: number, echecs: number, ignores: number}>} le bilan
   */
  executer: () => api.post('/envois/executer'),
  /**
   * @param {number} id facture à envoyer, PDF joint
   * @returns {Promise<object>} la ligne du journal
   */
  envoieFacture: (id) => api.post(`/envois/factures/${id}/email`),
  /**
   * @param {number} id facture dont le rappel courant part, PDF joint
   * @returns {Promise<object>} la ligne du journal
   */
  envoieRappel: (id) => api.post(`/envois/factures/${id}/rappel/email`),
  /**
   * @param {number} id devis à envoyer, PDF joint
   * @param {number[]} [attestations] attestations de l'entreprise cochées, jointes au même
   *   e-mail et retenues sur l'offre ; absentes, l'offre part seule et garde son choix
   * @returns {Promise<object>} la ligne du journal
   */
  envoieDevis: (id, attestations) =>
    Array.isArray(attestations)
      ? api.post(`/envois/devis/${id}/email`, { attestations })
      : api.post(`/envois/devis/${id}/email`),
  /**
   * @param {number} id offre concernée
   * @returns {Promise<{attestations: number[]}>} les attestations retenues pour elle
   */
  attestationsDeLOffre: (id) => api.get(`/envois/devis/${id}/attestations`),
  /**
   * Retient les attestations cochées pour une offre : la page du client, que
   * le lien WhatsApp ou SMS ouvre, les montre.
   *
   * @param {number} id offre concernée
   * @param {number[]} attestations attestations cochées ; vide, plus aucune
   * @returns {Promise<{attestations: number[]}>} les attestations retenues
   */
  retiensAttestations: (id, attestations) =>
    api.put(`/envois/devis/${id}/attestations`, { attestations }),
  /**
   * Envoie des attestations de l'entreprise, seules, à une adresse choisie.
   *
   * @param {{email: string, message?: string, attestations: number[]}} envoi à qui, quoi
   * @returns {Promise<object>} la ligne du journal
   */
  envoieAttestations: (envoi) => api.post('/envois/attestations/email', envoi),
  /**
   * Envoie un plan de chantier au client, fichier joint. Le drapeau « envoyé »
   * du plan ne se pose qu'une fois le message remis.
   *
   * @param {number} chantierId chantier concerné
   * @param {number} planId plan à envoyer
   * @returns {Promise<object>} la ligne du journal
   */
  envoiePlan: (chantierId, planId) =>
    api.post(`/envois/chantiers/${chantierId}/plans/${planId}/email`),
  /**
   * Envoie un courrier de l'écran Courrier, en PDF, par e-mail.
   *
   * @param {{email: string, dest: string, destAdr?: string, objet?: string, corps: string, logo?: string}} lettre
   * @returns {Promise<object>} la ligne du journal
   */
  envoieLettre: (lettre) => api.post('/envois/lettre/email', lettre),
  /**
   * @param {string} email adresse de la comptable ou de la fiduciaire
   * @param {number[]} factureIds factures choisies, PDF joints ; trente au plus
   * @returns {Promise<object>} la ligne du journal
   */
  envoieAuComptable: (email, factureIds) =>
    api.post('/envois/comptable/email', { email, factureIds }),
  /**
   * Des dépenses choisies, en un PDF (la liste puis chaque pièce), par e-mail.
   *
   * @param {string} email adresse du destinataire
   * @param {object} dossier le PDF demandé, comme pour l'imprimer
   * @param {number} nombre nombre de dépenses choisies
   * @returns {Promise<object>} la ligne du journal
   */
  envoieDepenses: (email, dossier, nombre) =>
    api.post('/envois/depenses/email', { email, dossier, nombre }),
  /**
   * @param {number} id décharge à envoyer au client, PDF joint
   * @param {string} [adresse] e-mail saisi à l'envoi, quand la fiche du client n'en a pas
   * @returns {Promise<object>} la ligne du journal
   */
  envoieDecharge: (id, adresse) =>
    api.post(
      `/envois/decharges/${id}/email` + (adresse ? '?a=' + encodeURIComponent(adresse) : ''),
    ),
  /**
   * @returns {Promise<object>} la boîte e-mail de l'entreprise, sans son mot de passe
   */
  boite: () => api.get('/envois/boite'),
  /**
   * Raccorde la boîte e-mail de l'entreprise ; le serveur s'y connecte d'abord.
   *
   * @param {object} boite adresse, serveur, port, sécurité, identifiant, mot de passe
   * @returns {Promise<object>} la boîte enregistrée
   */
  enregistreBoite: (boite) => api.put('/envois/boite', boite),
  /**
   * @param {string} adresse adresse e-mail saisie
   * @returns {Promise<object>} son fournisseur et son serveur d'envoi, ou un objet vide
   */
  fournisseurBoite: (adresse) => api.get('/envois/boite/fournisseur', { adresse }),
  /** @returns {Promise<void>} */
  supprimeBoite: () => api.delete('/envois/boite'),
  /** @returns {Promise<{adresse: string}>} l'adresse qui a reçu l'e-mail d'essai */
  essaiBoite: () => api.post('/envois/boite/essai'),
};

/** Sauvegarde complète de la société (patron). */
export const exportApi = {
  /**
   * Toutes les collections de la société, en un objet.
   *
   * @returns {Promise<object>} société, clients, devis, factures, chantiers,
   *   employés, stock, catalogue, dépenses, ventes, commandes, livraisons,
   *   rendez-vous, feuilles et congés
   */
  tout: () => api.get('/export'),
};

/**
 * Véhicules de l'entreprise : leasing et achats, pour la charge de chaque
 * année. Leurs documents (contrat, facture, permis) sont des pièces jointes
 * « VEHICULE ».
 */
export const vehiculesApi = {
  /** @returns {Promise<object[]>} les véhicules */
  liste: () => api.get('/vehicules'),
  /** @param {object} v fiche @returns {Promise<object>} la fiche enregistrée */
  cree: (v) => api.post('/vehicules', v),
  /** @param {number} id véhicule @param {object} v fiche @returns {Promise<object>} la fiche */
  metAJour: (id, v) => api.put(`/vehicules/${id}`, v),
  /** @param {number} id véhicule à supprimer, avec ses documents */
  supprime: (id) => api.delete(`/vehicules/${id}`),
};

/**
 * Relevés de banque importés dans Compta → Banque : chaque fichier (PDF ou
 * camt.053) est gardé tel quel par le serveur, avec ce qui en a été lu, puis
 * montré dans le classeur et envoyé à la comptable.
 */
export const relevesBanqueApi = {
  /** @returns {Promise<object[]>} les relevés gardés, le plus récent d'abord */
  liste: () => api.get('/releves-banque'),
  /**
   * Garde le fichier d'un relevé importé. Le même fichier importé une seconde
   * fois rend le relevé déjà gardé (`deja` vrai), sans en créer un autre.
   *
   * @param {Blob} fichier le relevé, tel que téléchargé de l'e-banking
   * @param {{banque?: string, debut?: string, fin?: string, lignes?: number, entrees?: number, sorties?: number}} lu
   *   ce qui en a été lu (jours « AAAA-MM-JJ »)
   * @returns {Promise<{releve: object, deja: boolean}>} le relevé gardé
   */
  garde: (fichier, lu = {}) => {
    const corps = new FormData();
    corps.append('fichier', fichier, fichier.name || 'releve');
    for (const [cle, valeur] of Object.entries(lu)) {
      if (valeur !== undefined && valeur !== null && valeur !== '') {
        corps.append(cle, String(valeur));
      }
    }
    return api.post('/releves-banque', corps);
  },
  /**
   * @param {number} id relevé gardé
   * @returns {string} adresse du fichier, tel que transmis
   */
  adresseFichier: (id) => adresseApi(`/api/releves-banque/${id}/fichier`),
  /**
   * Envoie les relevés choisis à la comptable, chacun joint tel que transmis.
   *
   * @param {string} email adresse de la comptable
   * @param {{id: number, lignes?: object[]}[]} releves relevés choisis et, pour
   *   le résumé, leurs lignes avec ce à quoi chacune a été reliée
   * @returns {Promise<object>} la ligne du journal
   */
  envoieAuComptable: (email, releves) =>
    api.post('/releves-banque/envoi-comptable', { email, releves }),
};

/**
 * Les heures prévues d'un chantier : tapées à la main, sinon celles des lignes
 * en heures de l'offre et des avenants.
 */
export const heuresPrevuesApi = {
  /** @param {number} id chantier @returns {Promise<{saisies: ?number, offre: number, prevues: ?number}>} */
  lit: (id) => api.get(`/chantiers/${id}/heures-prevues`),
  /** @param {number} id chantier @param {?number} saisies heures, ou null pour celles de l'offre */
  ecrit: (id, saisies) => api.put(`/chantiers/${id}/heures-prevues`, { saisies }),
};
